import {Injectable} from '@angular/core';
import {FilterOption} from "../interface/filter-option";
import {FilterOptions} from "../interface/filter-options";
import {FilterKey} from "../interface/filter-key";
import {FilterControl} from "../interface/filter-control";
import {FilterProductPreview} from "../interface/filter-product-preview";
import {FilterRangeOption} from "../interface/filter-range-option";

@Injectable({
  providedIn: 'root'
})
export class FilterControlPrepService {

	constructor() {
	}

	private _prepareFilterOptionsFromValues(values: string[]): FilterOption[] {

		const options: FilterOption[] = [];

		values.forEach((value: string, index: number) => options.push({
			key: index,
			value: value,
			active: false
		}));

		return options;
	}

	private _prepareFilterOptionsFromKey(
		key: string,
		previews: FilterProductPreview[]
	): FilterOptions {

		const values: string[] = [];

		previews.forEach(preview => values.push(preview[key]));

		const uniqueValues: string[] = values.filter(
			(value: string, index: number, self: string[]) => self.indexOf(value) === index
		);

		return {
			options: this._prepareFilterOptionsFromValues(
				uniqueValues
			)
		};
	}

	private _prepareFilterOptionsFromKeyAndCsv(
		key: string,
		previews: FilterProductPreview[]
	): FilterOptions {

		const values: string[] = [];

		previews.forEach(preview => values.push(preview[key]));
		const uniqueValues: string[] = [...new Set(values.join(',').split(','))];

		return {
			options: this._prepareFilterOptionsFromValues(
				uniqueValues
			)
		};
	}

	private _prepareRangeOptionsFromKey(
		key: string,
		previews: FilterProductPreview[]
	): FilterRangeOption {

		const values: number[] = previews.map(product => product[key]);

		const max: number = Math.max(...values);
		const min: number = Math.min(...values);

		return {
			active: false,
			defaultMax: max,
			defaultMin: min,
			key: 1,
			max: max,
			min: min,
			minGap: 1,
			value1: min,
			value2: max
		}
	}

	private _prepareFilterOptionsAndSubOptionsFromKeyAndSubKey(
		key: string,
		subKey: string,
		previews: FilterProductPreview[]
	): FilterOptions {

		const options: FilterOption[] = [];

		previews.forEach((preview, index) => {
			const keyValue = preview[key];
			const subKeyValue = preview[subKey];

			// Check if the option already exists
			let option = options.find(opt => opt.value === keyValue);

			if (option === undefined) {
				// Create a new option if it doesn't exist
				option = {
					key: options.length + 1, // Assign a unique key, you may need a more robust method
					value: keyValue,
					active: false,
					subOptions: []
				};
				options.push(option);
			}
			if (option.subOptions !== undefined) {
				// Check if the sub-option already exists
				if (!option.subOptions.some(subOpt => subOpt.value === subKeyValue)) {
					// Create a new sub-option if it doesn't exist
					option.subOptions.push({
						key: option.subOptions.length + 1, // Assign a unique key, you may need a more robust method
						value: subKeyValue,
						active: false
					});
				}
			}
		});

		return {
			options: options
		};
	}

	public prepareFilterControlFromKeyAndSubKey(
		key: FilterKey,
		previews: FilterProductPreview[]
	): FilterControl {
		return {
			title: key.name,
			key: key,
			cohort: this._prepareFilterOptionsAndSubOptionsFromKeyAndSubKey(
				key.key,
				key.subKey? key.subKey : '',
				previews
			)
		};
	}

	public prepareFilterControlFromKey(
		key: FilterKey,
		previews: FilterProductPreview[]
	): FilterControl {

		switch (key.type) {
			case "sub":
				return this.prepareFilterControlFromKeyAndSubKey(
					key,
					previews
				);
			case "csv":
				return {
					title: key.name,
					key: key,
					cohort: this._prepareFilterOptionsFromKeyAndCsv(
						key.key,
						previews
					)
				}
			case "range":
				return {
					title: key.name,
					key: key,
					rangeCohort: this._prepareRangeOptionsFromKey(
						key.key,
						previews
					)
				}
			case "toggle":
				return {
					title: key.name,
					key: key,
					cohort: {
						options: [
							{
								key: 0,
								value: key.name,
								active: false
							}
						]
					}
				}
			default:
				return {
					title: key.name,
					key: key,
					cohort: this._prepareFilterOptionsFromKey(
						key.key,
						previews
					)
				};
		}


	}

}
